import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  Signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import {
  ActionModalComponent,
  availableLevels,
  CoordinatesModel,
  DateFormats,
  formatDateToString,
  GameDetailsModel,
  gameLevelMapper,
  locationTypeMapper,
  MODAL_TOKEN,
  ModalProvider,
  NAVIGATION_TOKEN,
  NavigationProvider,
  paymentTypesMapper,
  PresentModalComponent,
  PreventMultiClickDirective,
  reversedGameLevelMapper,
  reversedPaymentTypesMapper,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { combineLatest, combineLatestWith, EMPTY, filter, map, Observable, switchMap, take, tap } from 'rxjs';
import {
  CreateGameCommandHandler,
  CreateGameCommandResult,
  GetHostTeamsQueryHandler,
  GetHostTeamsQueryResult,
  UpdateGameCommandHandler
} from '../../application/handlers';
import {
  PageActionsComponent,
  PageLayoutComponent,
  SelectableMultiCheckboxModalComponent,
  SelectableMultiCheckboxModel,
  SelectableSingleRadioModalComponent,
  SingleRadioItemModel,
  TitileNavbarComponent
} from '@ui-components';
import { CreateGameViewService } from '../../application/view-services';
import { CreateGameSuccessModalComponent } from './success-modal/create-game-success-modal.component';
import { ActivatedRoute, Params } from '@angular/router';
import { Loader } from '@googlemaps/js-api-loader';
import { NgxGpAutocompleteDirective, NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { environment } from '../../../../../../src/environment/environment';
import { fromZonedTime } from 'date-fns-tz';
import { addDays, Day, formatISO, isBefore, nextDay } from 'date-fns';
import { GameTypeModalComponent } from './game-type-modal/game-type-modal.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppGameLocationType, GameLevelEnum, PaymentTypes, PriceCurrency, Visibility } from '@core-http';

@Component({
  selector: 'lib-create-game',
  templateUrl: './create-game.component.html',
  styleUrls: ['./create-game.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TitileNavbarComponent,
    NgxGpAutocompleteModule,
    PageLayoutComponent,
    PageActionsComponent,
    PreventMultiClickDirective
  ],
  providers: [
    CreateGameCommandHandler,
    UpdateGameCommandHandler,
    GetHostTeamsQueryHandler,
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: environment.mapApiKey,
        libraries: ['places']
      })
    }
  ]
})
export class CreateGameComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('placesRef') placesRef!: NgxGpAutocompleteDirective;

  public preventToDoubleClick: boolean = false;

  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly createGameCommandHandler: CreateGameCommandHandler = inject(CreateGameCommandHandler);
  private readonly updateGameCommandHandler: UpdateGameCommandHandler = inject(UpdateGameCommandHandler);
  private readonly createGameViewService: CreateGameViewService = inject(CreateGameViewService);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly getHostTeamsQueryHandler: GetHostTeamsQueryHandler = inject(GetHostTeamsQueryHandler);

  readonly hostTeams: WritableSignal<GetHostTeamsQueryResult[]> = signal([]);
  readonly isEditModeSignal: WritableSignal<boolean> = signal(false);
  readonly showTeamSelector: Signal<boolean> = computed(() => this.hostTeams().length >= 1);
  readonly isTeamSelectorReadonly: Signal<boolean> = computed(() => this.hostTeams().length === 1);

  readonly previousGame$: Observable<GameDetailsModel | null> = this.createGameViewService.previousGame$;

  readonly isRecurring$: Observable<boolean> = this.createGameViewService.isRecurring$;

  readonly previousGame: GameDetailsModel | null = this.createGameViewService.previousGame;

  readonly previousVisibility$: Observable<Visibility | undefined> = this.previousGame$.pipe(
    filter((v) => !!v),
    map((data: GameDetailsModel | null) => data?.visibility)
  );

  readonly isEditMode$: Observable<boolean> = this.activatedRoute.queryParams.pipe(
    map((params: Params) => params['mode'] === 'edit')
  );

  readonly navbarTitle$: Observable<string> = combineLatest([this.previousGame$, this.isEditMode$]).pipe(
    map(([isPrevious, isEdit]: [GameDetailsModel | null, boolean]) =>
      !isPrevious || (isPrevious && !isEdit) ? 'Dodaj nową gierkę' : 'Edytuj gierkę'
    )
  );

  readonly okButtonTitile$: Observable<string> = combineLatest([this.previousGame$, this.isEditMode$]).pipe(
    map(([data, isEdit]: [GameDetailsModel | null, boolean]) =>
      data && isEdit ? 'Edytuj gierkę' : 'Dodaj nową gierkę!'
    )
  );

  readonly previousRecurring$: Observable<boolean> = this.previousGame$.pipe(
    combineLatestWith(this.isRecurring$),
    map(([data, isRecurring]: [GameDetailsModel | null, boolean]) => (data?.isRecurring || isRecurring) ?? false)
  );

  startDate$: Observable<string | null> = this.isEditMode$.pipe(
    take(1),
    map((isEditMode: boolean) => {
      if (isEditMode) {
        const startValue = this.createGameForm.get('startDate')?.value;
        const utcDate: Date = new Date(startValue);
        const timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

        return formatISO(fromZonedTime(utcDate, timezone));
      }

      return null;
    })
  );

  public VisibilityEnum = Visibility;
  public isPaymentTypeVisible: boolean = false;

  public autoCompleteOptions = {
    componentRestrictions: { country: 'PL' }
  };

  readonly createGameForm: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.maxLength(25)]),
    startDate: new FormControl('', [Validators.required]),
    duration: new FormControl(90, [Validators.required, Validators.min(30)]),
    address: new FormControl('', [Validators.required]),
    city: new FormControl(),
    coordinates: new FormControl('', [Validators.required]),
    totalSlots: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[0-9]+$/),
      Validators.min(1),
      Validators.max(999)
    ]),
    level: new FormControl(GameLevelEnum.BEGINNER, [Validators.required]),
    priceAmount: new FormControl('', [
      Validators.required,
      Validators.pattern(/^[0-9]+$/),
      Validators.min(0),
      Validators.max(999)
    ]),
    paymentTypes: new FormControl([PaymentTypes.BLIK, PaymentTypes.TRANSFER, PaymentTypes.CASH], [Validators.required]),
    createGamePlayerForHost: new FormControl(true, [Validators.required]),
    description: new FormControl('', [Validators.maxLength(1000)]),
    visibility: new FormControl(Visibility.PUBLIC),
    phone: new FormControl('', [Validators.minLength(9), Validators.maxLength(9), Validators.pattern('^[0-9]*$')]),
    isRecurring: new FormControl(false),
    locationType: new FormControl(AppGameLocationType.OPEN_FIELD),
    teamId: new FormControl(null)
  });

  get getGameLevelValue(): string {
    const value: GameLevelEnum = this.createGameForm.get('level')?.value;
    return gameLevelMapper[value];
  }

  get getLocationTypeValue(): string {
    const value: AppGameLocationType = this.createGameForm.get('locationType')?.value;
    return locationTypeMapper[value].name;
  }

  get getPaymenValue(): string {
    if (!this.isPaymentTypeVisible) return 'Darmowa';

    const value: PaymentTypes[] = this.createGameForm.get('paymentTypes')?.value;
    return value.map((payment) => paymentTypesMapper[payment]).join(', ');
  }

  get isFreeGame(): boolean {
    return !this.createGameForm.get('priceAmount')?.value;
  }

  get getTeamSelectorValue(): string {
    const teamId: string | null = this.createGameForm.get('teamId')?.value;
    if (!teamId) return '';
    const team = this.hostTeams().find((t) => t.teamId === teamId);
    return team ? team.teamName : '';
  }

  ngOnInit(): void {
    this.isEditMode$.pipe(take(1)).subscribe((isEdit: boolean) => this.isEditModeSignal.set(isEdit));
    this.prepopulateForm();
    this.prepopulatePhone();
    this.loadHostTeams();
  }

  ngAfterViewInit(): void {
    this.showGameTypeModal();

    this.createGameForm
      .get('priceAmount')
      ?.valueChanges.pipe(tap(() => this.priceChanged()))
      .subscribe();
  }

  ngOnDestroy() {
    this.createGameViewService.resetPreviousGame();
  }

  get nameValueInvalid(): boolean {
    return this.createGameForm.get('name')?.value?.length > 25;
  }

  create() {
    if (this.preventToDoubleClick) return;

    this.markAsTouched();

    combineLatest([this.isEditMode$, this.previousGame$])
      .pipe(
        take(1),
        switchMap(([isEditMode, previousGame]) => {
          if (isEditMode) {
            return this.createGameForm.valid
              ? this.updateGameCommandHandler
                  .updateGame(previousGame?.gameId as string, {
                    ...this.setFormValues(),
                    gameContactPhone: this.setFormValues().gameContactPhone
                      ? `+48${this.setFormValues().gameContactPhone}`
                      : null,
                    priceAmount: this.setFormValues().priceAmount?.toString(),
                    paymentTypes: !this.setFormValues().priceAmount ? [] : this.setFormValues().paymentTypes
                  })
                  .pipe(
                    take(1),
                    tap((game: CreateGameCommandResult) => this.openSuccessModal(game, true)),
                    tap(() => (this.preventToDoubleClick = true))
                  )
              : EMPTY;
          } else {
            return this.createGameForm.valid
              ? this.createGameCommandHandler.createGame(this.setFormValues()).pipe(
                  take(1),
                  tap((game: CreateGameCommandResult) => this.openSuccessModal(game, false)),
                  tap(() => (this.preventToDoubleClick = true))
                )
              : EMPTY;
          }
        }),
        take(1)
      )
      .subscribe();
  }

  setGameType(): void {
    this.isRecurring$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((isRecurring: boolean) => {
          return this.createGameForm.patchValue({
            isRecurring: isRecurring
          });
        })
      )
      .subscribe();
  }

  getCoordinates(): CoordinatesModel {
    return {
      latitude:
        this.previousGame?.location?.coordinates?.latitude ?? this.createGameForm.get('coordinates')?.value.latitude,
      longitude:
        this.previousGame?.location?.coordinates?.longitude ?? this.createGameForm.get('coordinates')?.value.longitude
    };
  }

  handleAddressChange(address: google.maps.places.PlaceResult): void {
    const city = (): string | undefined => {
      if (address.address_components) {
        const localityComponent = address.address_components.find((component) => component.types.includes('locality'));
        if (localityComponent) {
          return localityComponent.long_name;
        }
      }
      return undefined;
    };

    this.createGameForm.patchValue({
      address: address.name,
      coordinates: {
        latitude: address?.geometry?.location?.lat(),
        longitude: address?.geometry?.location?.lng()
      },
      city: city()
    });
  }

  changeStartDate(event: any) {
    const selectedDate: Date = new Date(event.detail.value);

    const timeDiff: number = Math.abs(selectedDate.getTime() - new Date().getTime());

    if (timeDiff < 3600000 || selectedDate.getTime() < new Date().getTime()) {
      this.modalProvider.showModal$({
        component: PresentModalComponent,
        componentProps: {
          header: 'Ustaw poprawną datę',
          message: 'Nie możesz dodać gierki, ktora rozpoczyna sie wcześniej niż za godzinę.',
          btnTxt: 'Zamknij'
        },
        cssClass: 'present-modal'
      });
    } else if (timeDiff > 2678400000) {
      this.modalProvider.showModal$({
        component: PresentModalComponent,
        componentProps: {
          header: 'Ustaw poprawną datę',
          message: 'Nie możesz dodać gierki, ktora rozpoczyna sie za ponad 31 dni',
          btnTxt: 'Zamknij'
        },
        cssClass: 'present-modal'
      });
    } else {
      this.createGameForm.patchValue({
        startDate: selectedDate.toISOString()
      });
    }
  }

  createGamePlayerForHost(event: any) {
    this.createGameForm.patchValue({
      createGamePlayerForHost: event.detail.checked
    });
  }

  markAsPrivateGame(event: any) {
    this.createGameForm.patchValue({
      visibility: event.detail.checked ? Visibility.PRIVATE : Visibility.PUBLIC
    });
  }

  markAsIsRecurring(event: any) {
    this.createGameForm.patchValue({
      isRecurring: event.detail.checked
    });
  }

  get getFormattedDate(): string {
    const isoDate = this.createGameForm.get('startDate')?.value;

    if (!this.createGameForm.get('startDate')?.value) return '';

    return `${formatDateToString(isoDate, DateFormats.DayMonthYearDot)} | ${formatDateToString(isoDate, DateFormats.Time)}`;
  }

  markAsTouched(): void {
    Object.keys(this.createGameForm.controls).forEach((field: string) => {
      const control = this.createGameForm.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control: AbstractControl<unknown, unknown, unknown> | null = this.createGameForm.get(fieldName);
    return !!control?.touched && !!control?.invalid;
  }

  private addMinutesToDate(dateString: string, minutes: number): string {
    return new Date(new Date(dateString).getTime() + minutes * 60000).toISOString();
  }

  private prepopulateForm(): void {
    combineLatest([this.previousGame$, this.isEditMode$])
      .pipe(
        take(1),
        map(([previousGame, isEdit]: [GameDetailsModel | null, boolean]) => {
          if (previousGame) {
            const adjustedStartDate: Date = this.calculateNextGameDate(new Date(previousGame?.gameDateStart), isEdit);

            this.createGameForm.patchValue({
              name: previousGame?.gameName,
              totalSlots: previousGame?.totalSlots,
              level: reversedGameLevelMapper[previousGame?.level],
              priceAmount: previousGame?.priceAmount?.toString(),
              paymentTypes: previousGame?.paymentType?.map((type: string) => reversedPaymentTypesMapper[type]),
              description: previousGame?.description,
              duration: previousGame?.duration,
              coordinates: previousGame?.location?.coordinates,
              address: previousGame?.location?.address,
              city: previousGame?.location?.name?.split(',')?.[1]?.trim(),
              isRecurring: previousGame?.isRecurring,
              startDate: adjustedStartDate?.toISOString(),
              visibility: previousGame?.visibility,
              locationType: previousGame?.locationType
            });

            if (isEdit) {
              this.createGameForm.patchValue({ teamId: previousGame?.teamId ?? null });
            }
          }
        }),
        tap(() => this.priceChanged())
      )
      .subscribe();
  }

  private calculateNextGameDate(previousStartDate: Date, isEdit: boolean): Date {
    const getDayAsDay = (day: number): Day => {
      return day as Day;
    };

    const setSameTime = (baseDate: Date, referenceDate: Date): Date => {
      const result: Date = new Date(baseDate);
      result.setHours(referenceDate.getHours());
      result.setMinutes(referenceDate.getMinutes());
      result.setSeconds(referenceDate.getSeconds());
      result.setMilliseconds(referenceDate.getMilliseconds());
      return result;
    };

    if (isEdit) {
      return previousStartDate;
    }

    if (isBefore(previousStartDate, new Date())) {
      const weekday: Day = getDayAsDay(previousStartDate.getDay());
      const nextDate: Date = nextDay(new Date(), weekday);
      return setSameTime(nextDate, previousStartDate);
    }

    const nextWeekDate: Date = addDays(previousStartDate, 7);
    return setSameTime(nextWeekDate, previousStartDate);
  }

  private prepopulatePhone() {
    this.userDataProvider.userData$
      .pipe(
        take(1),
        tap((userData: UserModel) => this.createGameForm.patchValue({ phone: userData.phone }))
      )
      .subscribe();
  }

  private openSuccessModal(game: CreateGameCommandResult, isEdit: boolean): void {
    this.modalProvider.showModal$({
      component: CreateGameSuccessModalComponent,
      componentProps: {
        game: game as unknown as GameDetailsModel,
        isEdit: isEdit
      },
      cssClass: 'present-modal',
      backdropDismiss: false
    });
  }

  private setFormValues() {
    return {
      name: this.createGameForm.get('name')?.value,
      gameDateStart: this.createGameForm.get('startDate')?.value,
      gameDateEnd: this.addMinutesToDate(
        this.createGameForm.get('startDate')?.value,
        this.createGameForm.get('duration')?.value
      ),
      address: this.createGameForm.get('address')?.value,
      city: this.createGameForm.get('city')?.value,
      coordinates: this.getCoordinates(),
      totalSlots: +this.createGameForm.get('totalSlots')?.value,
      level: this.createGameForm.get('level')?.value,
      priceAmount: this.createGameForm.get('priceAmount')?.value,
      paymentTypes: this.createGameForm.get('paymentTypes')?.value,
      description: this.createGameForm.get('description')?.value,
      createGamePlayerForHost: this.createGameForm.get('createGamePlayerForHost')?.value,
      priceCurrency: PriceCurrency.PLN,
      visibility: this.createGameForm.get('visibility')?.value,
      gameContactPhone: this.createGameForm.get('phone')?.value,
      isRecurring: this.createGameForm.get('isRecurring')?.value,
      locationType: this.createGameForm.get('locationType')?.value,
      teamId: this.createGameForm.get('teamId')?.value ?? undefined
    };
  }

  private loadHostTeams(): void {
    this.getHostTeamsQueryHandler
      .getHostTeams()
      .pipe(
        take(1),
        tap((teams: GetHostTeamsQueryResult[]) => {
          this.hostTeams.set(teams);

          if (!this.isEditModeSignal() && teams?.length) {
            this.createGameForm.patchValue({ teamId: teams[0].teamId });
          }

          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  private priceChanged(): void {
    this.createGameForm.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        tap((form) => {
          this.isPaymentTypeVisible = +form.priceAmount > 0;

          if (+form.priceAmount < 1 || form.priceAmount === '')
            return this.createGameForm?.get('paymentTypes')?.disable();

          return this.createGameForm?.get('paymentTypes')?.enable();
        })
      )
      .subscribe();
  }

  private showGameTypeModal(): void {
    combineLatest([this.isEditMode$, this.previousGame$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        filter(([isEdit, previousGame]: [boolean, GameDetailsModel | null]) => !isEdit && !previousGame),
        switchMap(() =>
          this.modalProvider.showModal$({
            component: ActionModalComponent,
            cssClass: 'present-modal',
            backdropDismiss: false,
            componentProps: {
              header: 'Jaką gierkę chciałbyś stworzyć?',
              message: 'Typ gierki będzisz mógł zmienić w panelu dodawania oraz eydcji gierki.',
              btnOk: 'Przejdź dalej',
              btnCancel: 'Anuluj',
              template: GameTypeModalComponent,
              action: () => this.setGameType(),
              cancelAction: () => this.navigationProvider.back()
            }
          })
        )
      )
      .subscribe();
  }

  openTeamSelectorModal(): void {
    if (this.isTeamSelectorReadonly()) return;

    const selectedTeamId: string | null = this.createGameForm.get('teamId')?.value;

    const payload: SingleRadioItemModel<string>[] = this.hostTeams().map(
      (team: GetHostTeamsQueryResult): SingleRadioItemModel<string> => ({
        name: team.teamName + ' (' + team.membersCount + ' graczy)',
        value: team.teamId
      })
    );

    this.modalProvider
      .showModal$<string>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        backdropDismiss: false,
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Drużyna',
          providedData: payload,
          selectedData: selectedTeamId
        }
      })
      .pipe(take(1))
      .subscribe((result: string) => {
        this.createGameForm.patchValue({ teamId: result });
        this.cdr.detectChanges();
      });
  }

  openGameLevelModal(): void {
    const selectedLevel: GameLevelEnum = this.createGameForm.get('level')?.value as GameLevelEnum;

    const payload: SingleRadioItemModel<GameLevelEnum>[] = availableLevels.map((level: GameLevelEnum) => {
      return { name: gameLevelMapper[level], value: level };
    });

    this.modalProvider
      .showModal$<GameLevelEnum>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        backdropDismiss: false,
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Poziom gry',
          providedData: payload,
          selectedData: selectedLevel
        }
      })
      .pipe(take(1))
      .subscribe((result: GameLevelEnum) => {
        this.createGameForm.patchValue({ level: result });
        this.cdr.detectChanges();
      });
  }

  openLocationTypeModal(): void {
    const selectedLocation: AppGameLocationType = this.createGameForm.get('locationType')?.value as AppGameLocationType;

    const payload: SingleRadioItemModel<AppGameLocationType>[] = Object.values(AppGameLocationType).map(
      (location: AppGameLocationType) => {
        return { name: locationTypeMapper[location].name, value: location };
      }
    );

    this.modalProvider
      .showModal$<AppGameLocationType>({
        component: SelectableSingleRadioModalComponent,
        cssClass: 'present-modal',
        backdropDismiss: false,
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Typ boiska',
          providedData: payload,
          selectedData: selectedLocation
        }
      })
      .pipe(take(1))
      .subscribe((result: AppGameLocationType) => {
        this.createGameForm.patchValue({ locationType: result });
        this.cdr.detectChanges();
      });
  }

  openPaymentModal(): void {
    if (!this.isPaymentTypeVisible) return;

    const selectedPayments: PaymentTypes[] = this.createGameForm.get('paymentTypes')?.value;

    const payload: SelectableMultiCheckboxModel<PaymentTypes>[] = Object.values(PaymentTypes).map(
      (payment: PaymentTypes) => {
        return { name: paymentTypesMapper[payment], value: payment };
      }
    );

    this.modalProvider
      .showModal$<PaymentTypes[]>({
        component: SelectableMultiCheckboxModalComponent,
        cssClass: 'present-modal',
        backdropDismiss: false,
        componentProps: {
          buttonText: 'Wybierz',
          header: 'Typ płatności',
          providedData: payload,
          selectedData: selectedPayments,
          requireSelection: true
        }
      })
      .pipe(take(1))
      .subscribe((result: PaymentTypes[]) => {
        this.createGameForm.patchValue({ paymentTypes: result });
        this.cdr.detectChanges();
      });
  }
}
